import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import Pagination, { usePagination } from '../components/Pagination';
import ToOrderList from '../components/suppliers/ToOrderList';
import ReceivedPrompt from '../components/suppliers/ReceivedPrompt';
import VendorCard from '../components/vendors/VendorCard';
import VendorForm from '../components/vendors/VendorForm';
import PurchaseOrderCard from '../components/purchaseOrders/PurchaseOrderCard';
import PurchaseOrderForm from '../components/purchaseOrders/PurchaseOrderForm';
import { CalendarIcon, GridIcon, SearchIcon, SparkleIcon } from '../components/icons';
import { useAuth } from '../context/AuthContext';
import { addVendor, createVendor, deleteVendor, listVendors, updateVendor } from '../lib/vendors';
import {
  addPurchaseOrder,
  deletePurchaseOrder,
  listPurchaseOrders,
  poTotalJmd,
  setPurchaseOrderStatus,
  updatePurchaseOrder,
} from '../lib/purchaseOrders';
import { listReorderItems, markItemsOrdered, syncItemsToOrderStatus } from '../lib/reorder';
import { addExpense } from '../lib/finances';
import { todayISO } from '../lib/dates';
import './SuppliersOrders.css';
import { useConfirm } from '../components/ConfirmDialog';

const TABS = [
  { id: 'To Order', label: 'To Order', Icon: GridIcon },
  { id: 'Purchase Orders', label: 'Purchase Orders', Icon: CalendarIcon },
  { id: 'Vendors', label: 'Vendors', Icon: SearchIcon },
];

const PO_FILTERS = ['All', 'Ordered', 'Shipped', 'Received', 'Cancelled'];

// Vendors, the reorder list and purchase orders in one section, because in
// practice they're one flow: notice a shortage → raise an order with a vendor
// you've already saved → receive it → record the expense.
export default function SuppliersOrders() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [tab, setTab] = useState(TABS[0].id);

  // Purchase Orders and Vendors each have a search box but shared one `query`
  // state, so a term typed on one tab silently filtered the other — you'd
  // switch to Vendors and find suppliers missing for no visible reason.
  // Switching tabs clears it; the placeholder differs per tab, so a carried
  // -over term reads as broken rather than as a filter you left on.
  const selectTab = (next) => {
    setTab(next);
    setQuery('');
  };

  const [vendors, setVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reorderItems, setReorderItems] = useState([]);
  const [reorderError, setReorderError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [vendorFormOpen, setVendorFormOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [poFormOpen, setPoFormOpen] = useState(false);
  const [editingPo, setEditingPo] = useState(null);
  const [poPrefill, setPoPrefill] = useState(null);
  const [poFilter, setPoFilter] = useState('All');
  const [receivedPrompt, setReceivedPrompt] = useState(null);
  const [query, setQuery] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // The reorder list is fetched separately and its failure contained —
      // it's the newest table, and the other two tabs must not go blank if
      // its migration hasn't been run yet.
      const reorderPromise = listReorderItems().then(
        (rows) => {
          setReorderItems(rows);
          setReorderError(null);
        },
        (err) => {
          setReorderItems([]);
          setReorderError(err.message);
        }
      );

      const [vendorRows, orderRows] = await Promise.all([listVendors(), listPurchaseOrders()]);
      await reorderPromise;
      setVendors(vendorRows);
      setOrders(orderRows);
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ---- vendors ----------------------------------------------------------
  const handleVendorSubmit = async (fields) => {
    if (editingVendor) await updateVendor(editingVendor.id, fields);
    else await createVendor(fields);
    setVendorFormOpen(false);
    setEditingVendor(null);
    await loadAll();
  };

  const handleVendorDelete = async (id) => {
    try {
      if (!(await confirm({
        title: 'Delete this vendor?',
        body: "Purchase orders raised against them will lose the link.",
      }))) return;

      await deleteVendor(id);
      await loadAll();
    } catch (err) {
      setLoadError(`Couldn't delete vendor: ${err.message}`);
    }
  };

  const handleCreateVendorInline = async (name) => {
    const vendor = await addVendor(name);
    setVendors((prev) => [...prev, vendor].sort((a, b) => a.name.localeCompare(b.name)));
    return vendor;
  };

  // ---- purchase orders --------------------------------------------------
  const handlePoSubmit = async (poFields, items, linkedItemIds = []) => {
    let saved;
    if (editingPo) {
      await updatePurchaseOrder(editingPo.id, poFields, items);
    } else {
      saved = await addPurchaseOrder({ ...poFields, created_by: user.id }, items);
      // Stamp the reorder items this order came from, so the list records
      // what was raised rather than the items just disappearing. The ids come
      // from the form, which knows both what arrived via `prefill` and what
      // was picked from its own To Order list.
      if (linkedItemIds.length && saved?.id) {
        await markItemsOrdered(linkedItemIds, saved.id);
      }
    }
    setPoFormOpen(false);
    setEditingPo(null);
    setPoPrefill(null);
    await loadAll();
  };

  const handleSetStatus = async (id, status, extra) => {
    await setPurchaseOrderStatus(id, status, extra);

    // The reorder items this order was raised from follow it: Received
    // completes them, Cancelled puts them back on the list. Contained like
    // the rest of the reorder feature, so a missing table can't block a
    // purchase order from changing status.
    try {
      await syncItemsToOrderStatus(id, status);
    } catch (err) {
      setLoadError(`Order updated, but its To Order items didn't: ${err.message}`);
    }

    await loadAll();
    // Prompt rather than write automatically: a status change that silently
    // books an expense can double up with one entered by hand.
    if (status === 'Received') {
      const order = orders.find((o) => o.id === id);
      if (order) setReceivedPrompt(order);
    }
  };

  const handleRecordExpense = async (category) => {
    const order = receivedPrompt;
    const items = order.purchase_order_items || [];
    await addExpense({
      entry_date: todayISO(),
      category,
      description: `${order.po_number} — ${order.vendor?.name || 'purchase order'}`,
      vendor: order.vendor?.name || null,
      currency: order.currency,
      amount: items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_cost || 0), 0),
      exchange_rate: order.exchange_rate,
      created_by: user.id,
    });
    setReceivedPrompt(null);
  };

  const handleCreatePOFromItems = (items) => {
    // VendorAutocomplete works on names, so prefill resolves the id back to
    // a name here rather than teaching the form about ids.
    setPoPrefill({
      vendorName: items[0]?.vendor?.name || '',
      itemIds: items.map((i) => i.id),
      items: items.map((i) => ({
        item_name: i.quantity ? `${i.item_name} (${i.quantity})` : i.item_name,
        quantity: 1,
        unit_cost: '',
        notes: i.notes || '',
      })),
    });
    setEditingPo(null);
    setPoFormOpen(true);
    selectTab('Purchase Orders');
  };

  // ---- derived ----------------------------------------------------------
  const q = query.trim().toLowerCase();

  const filteredOrders = orders.filter((o) => {
    if (poFilter !== 'All' && o.status !== poFilter) return false;
    if (!q) return true;
    // Line items are searched too. You look for an order by what's *in* it —
    // "silicone tray" — not by a PO number you've never had to memorise.
    return [o.po_number, o.vendor?.name || '', ...(o.purchase_order_items || []).map((i) => i.item_name)]
      .join(' ')
      .toLowerCase()
      .includes(q);
  });

  // Searching Purchase Orders for something that's still only on the To Order
  // list finds nothing, which reads as "it vanished". It hasn't been ordered
  // yet — so say so, and offer the step that turns it into an order.
  const unorderedMatches = q
    ? reorderItems.filter((i) => i.status === 'Needed' && i.item_name.toLowerCase().includes(q))
    : [];
  const matchVendorIds = [...new Set(unorderedMatches.map((i) => i.vendor_id || 'none'))];
  const matchesOneVendor = matchVendorIds.length === 1;

  const filteredVendors = vendors.filter((v) => {
    if (!q) return true;
    return `${v.name} ${v.supplies || ''}`.toLowerCase().includes(q);
  });

  const { visible: visibleOrders, controls: orderPaging } = usePagination(filteredOrders);
  const { visible: visibleVendors, controls: vendorPaging } = usePagination(filteredVendors);

  const neededCount = reorderItems.filter((i) => i.status === 'Needed').length;
  const openOrders = orders.filter((o) => o.status === 'Ordered' || o.status === 'Shipped').length;

  return (
    <>
      <PageHeader
        title={
          <>
            Suppliers &amp; Orders
            <SparkleIcon className="page-title-sparkle" />
          </>
        }
        subtitle="What you need to buy, what's on order, and who you buy it from."
        actions={
          tab === 'Vendors' && !vendorFormOpen ? (
            <button
              className="primary-action"
              onClick={() => {
                setEditingVendor(null);
                setVendorFormOpen(true);
              }}
            >
              + Add vendor
            </button>
          ) : tab === 'Purchase Orders' && !poFormOpen ? (
            <button
              className="primary-action"
              onClick={() => {
                setEditingPo(null);
                setPoPrefill(null);
                setPoFormOpen(true);
              }}
            >
              + New purchase order
            </button>
          ) : null
        }
      />

      <nav className="view-tabs">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            className={'view-tab' + (tab === id ? ' active' : '')}
            onClick={() => selectTab(id)}
          >
            <Icon />
            {label}
            {id === 'To Order' && neededCount > 0 && (
              <span className="tab-badge">{neededCount}</span>
            )}
            {id === 'Purchase Orders' && openOrders > 0 && (
              <span className="tab-badge">{openOrders}</span>
            )}
          </button>
        ))}
      </nav>

      {loadError && <p className="po-error">{loadError}</p>}

      {tab === 'To Order' && (
        <ToOrderList
          items={reorderItems}
          vendors={vendors}
          loading={loading}
          error={
            reorderError
              ? `Couldn't load the reorder list: ${reorderError}. Run migration 0022 if you haven't yet.`
              : null
          }
          onChanged={loadAll}
          onCreatePO={handleCreatePOFromItems}
          onCreateVendor={handleCreateVendorInline}
        />
      )}

      {tab === 'Purchase Orders' && (
        <>
          {poFormOpen && (
            <PurchaseOrderForm
              initialPO={editingPo}
              prefill={poPrefill}
              reorderItems={reorderItems}
              vendors={vendors}
              onCreateVendor={handleCreateVendorInline}
              onSubmit={handlePoSubmit}
              onCancel={() => {
                setPoFormOpen(false);
                setEditingPo(null);
                setPoPrefill(null);
              }}
            />
          )}

          <section className="panel">
            <div className="library-toolbar">
              <div className="library-search">
                <SearchIcon />
                <input
                  type="search"
                  placeholder="Search orders…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="po-filters">
                {PO_FILTERS.map((s) => (
                  <button
                    key={s}
                    className={'batch-filter' + (poFilter === s ? ' active' : '')}
                    onClick={() => setPoFilter(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <p className="po-loading">Loading…</p>
            ) : visibleOrders.length === 0 ? (
              unorderedMatches.length > 0 ? (
                <div className="po-empty po-empty-bridge">
                  <p className="po-empty-lead">
                    Nothing ordered yet matching &ldquo;{query.trim()}&rdquo;.
                  </p>
                  <p>
                    {unorderedMatches.length === 1
                      ? `"${unorderedMatches[0].item_name}" is on your To Order list — it becomes a purchase order once you raise one.`
                      : `${unorderedMatches.length} matching items are on your To Order list — they become purchase orders once you raise one.`}
                  </p>
                  {matchesOneVendor ? (
                    <button
                      className="primary-action"
                      onClick={() => handleCreatePOFromItems(unorderedMatches)}
                    >
                      Create purchase order from{' '}
                      {unorderedMatches.length === 1
                        ? 'it'
                        : `these ${unorderedMatches.length} items`}
                    </button>
                  ) : (
                    // A PO goes to one vendor, so a mixed match can't become
                    // one order — send them where they can pick.
                    <button className="ghost-button" onClick={() => selectTab('To Order')}>
                      Open To Order to choose
                    </button>
                  )}
                </div>
              ) : (
                <p className="po-empty">No purchase orders match.</p>
              )
            ) : (
              <div className="po-list">
                {visibleOrders.map((po) => (
                  <PurchaseOrderCard
                    key={po.id}
                    po={po}
                    onEdit={(order) => {
                      setEditingPo(order);
                      setPoFormOpen(true);
                    }}
                    onDelete={async (id) => {
                      if (!(await confirm({
                        title: 'Delete this purchase order?',
                        body: "Its line items go with it, and anything on the To Order list raised from it is released.",
                      }))) return;

                      await deletePurchaseOrder(id);
                      await loadAll();
                    }}
                    onSetStatus={handleSetStatus}
                  />
                ))}
              </div>
            )}

            <Pagination {...orderPaging} />
          </section>
        </>
      )}

      {tab === 'Vendors' && (
        <>
          {vendorFormOpen && (
            <VendorForm
              initialVendor={editingVendor}
              onSubmit={handleVendorSubmit}
              onCancel={() => {
                setVendorFormOpen(false);
                setEditingVendor(null);
              }}
            />
          )}

          <section className="panel">
            <div className="library-toolbar">
              <div className="library-search">
                <SearchIcon />
                <input
                  type="search"
                  placeholder="Search vendors or what they supply…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>

            {loading ? (
              <p className="po-loading">Loading…</p>
            ) : visibleVendors.length === 0 ? (
              <p className="vendor-empty">No vendors match.</p>
            ) : (
              <div className="vendor-list">
                {visibleVendors.map((vendor) => (
                  <VendorCard
                    key={vendor.id}
                    vendor={vendor}
                    orders={orders.filter((o) => o.vendor_id === vendor.id)}
                    onEdit={(v) => {
                      setEditingVendor(v);
                      setVendorFormOpen(true);
                    }}
                    onDelete={handleVendorDelete}
                  />
                ))}
              </div>
            )}

            <Pagination {...vendorPaging} />
          </section>
        </>
      )}

      {receivedPrompt && (
        <ReceivedPrompt
          order={receivedPrompt}
          totalJmd={poTotalJmd(
            receivedPrompt.purchase_order_items || [],
            receivedPrompt.exchange_rate
          )}
          onSkip={() => setReceivedPrompt(null)}
          onConfirm={handleRecordExpense}
        />
      )}
    </>
  );
}
